import { useCallback, useEffect, useRef, useState } from "react";

import { useFormik } from 'formik';
import * as yup from 'yup';
import { toast } from "sonner";
import { ActionIcon, Button, Input, Modal, Switch, Text, Textarea } from "rizzui";
import { IoClose } from "react-icons/io5";
import moment from "moment";
import { CarrionService } from "../../../services/carrion/carrionService";
import useHandleErrors from "../../../hooks/useHandleErrors";
import useCompanies from "../../../hooks/api-calls/useCompanies";
import useCarrionStatus from "../../../hooks/api-calls/useCarrionStatus";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import useFetch from "../../../hooks/useFetch";
import CustomSelect from "../../../components/forms/CustomSelect";
import FormLabel from "../../../components/forms/FormLabel";
import { DatePicker } from "../../../components/forms/DatePicker";
import CoordinatesInputs from "../../../components/commonForm/CoordinatesInputs";
import WeatherSelectors from "../../../components/commonForm/WeatherSelectors";
import EntitySelectors from "../../../components/commonForm/EntitySelectors";
import RequiredInput from "../../../components/forms/InputHOC";
import { age, sex } from "../../../utils/SelectorMapping";

interface ParkFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    refetch: () => void;
    carrion: string | null;
    isFromPark?: boolean;
    parkId?: string;
}


const schema = yup.object({
    parkId: yup.string().required('Campo requerido'),
    windTurbineId: yup.string().required('Campo requerido'),
    userId: yup.string().required('Campo requerido'),
    specieId: yup.string().required('Campo requerido'),
    huso: yup.string().required('Campo requerido').test('spain_timezone', 'Debe de ser un huso horario de España (29, 30, 31)', (value) => {
        if (value === null || value === undefined || value === "") return true;
        const regex = /^(29|30|31)/;
        return regex.test(value);
    }),
    coordinatesX: yup.string().required('Campo requerido').test('is-decimal', 'El valor debe de estar entre 160000 y 870000', function (value) {
        if (Number(value) >= 160000 && Number(value) <= 870000) {
            return true;
        } 
        return false;
    }),
    coordinatesY: yup.string().required('Campo requerido').test('is-decimal', 'El valor debe de estar entre 3990000 y 4800000', function (value) {
        if (Number(value) >= 3990000 && Number(value) <= 4800000) {
            return true;
        } 
        return false;
    }),
    dateReported: yup.string().required('Campo requerido'),
    distanceFromTurbine: yup
    .number()
    .typeError('La distancia debe ser un número o un decimal con punto')
    .test('min-zero', 'La distancia no puede ser negativa', (value) => {
        if (value === null || value === undefined || (value as unknown) === '') return true;
        return Number(value) >= 0;
    })
    .test(
      'is-decimal',
      'La distancia debe ser un número o un decimal con punto',
      value => {
        if (value === null || value === undefined || (value as unknown) === '') return true;
        return /^\d+(\.\d+)?$/.test(String(value));
      }
    )
    .required('Campo requerido'),
    notifiedEnvAgency: yup.boolean(),
    notifiedOwner: yup.boolean(),
    nameOwner: yup.string().test('notifiedOwner', 'Campo requerido', function (value) {
        if (this.parent.notifiedOwner == true) {
            return value !== undefined && value !== null && value !== '';
        }
        return true;
    }),
    avianPresencia: yup.boolean(),
    turbineOff: yup.boolean(),
    windDirection: yup.string().required('Campo requerido'),
    windSpeed: yup.string().required('Campo requerido'),
    comments: yup.string().nullable(),
    age: yup.string().required('Campo requerido'),
    cloudCoverage: yup.string().required('Campo requerido'),

});

const CarrionFormModal: React.FC<ParkFormModalProps> = ({isOpen, onClose, refetch, carrion, isFromPark, parkId }) => {
    
    // STATES

    const service = new CarrionService();
    const isEdit = carrion !== null;
    
    // HOOKS

    const { handleErrors } = useHandleErrors();
    const { getCompaniesList } = useCompanies();
    const { getCarrionStatusList } = useCarrionStatus();
    const { userCan } = usePrivilege();

    // DATA FETCHING

    //-----------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTENER CARROÑA A EDITAR
     * @EN GET CARRION TO EDIT
     */
    //-------------------------------------------------------------------------------------------------------------
    const [data, loading, error] = useFetch(
        useCallback(async () => {
            if (!carrion) return;
            const response = await service.getCarrionById(carrion);
            return response.getResponseData();
        },[carrion])
    );

    // FORMIK

    const formik = useFormik({
        initialValues: {
            id: '',
            parkId: parkId ? parkId : '',
            windTurbineId: '',
            userId: '', 
            specieId: '',
            age: '',
            cloudCoverage: '',
            windDirection: '',
            windSpeed: '',
            company: '',
            notifiedEnvAgency: false,
            notifiedOwner: false,
            nameOwner: '',
            avianPresencia: false,
            huso: '',
            coordinatesX: '',
            coordinatesY: '',
            distanceFromTurbine: '',
            dateReported: '',
            comments: '',  
            statusId: '',
            turbineOff: false,
            photos: null,
            sex: '',
        },
        validationSchema: schema,
        validateOnBlur: true,
        validateOnChange: true,
        onSubmit: values => { 
            if(!isEdit) {
                handleCreate(values)
            } else {
                handleEdit(values)
            }
        },
    });

    const appendFormValue = (formData: FormData, key: string, value: unknown) => {
        if (value === null || value === undefined) {
            return;
        }
        if (typeof value === 'boolean') {
            formData.append(key, value ? 'true' : 'false');
            return;
        }
        if (value === '') {
            return;
        }
        if (key === 'dateReported') {
            formData.append(key, moment(String(value), 'DD/MM/YYYY').format('YYYY-MM-DD'));
            return;
        }
        formData.append(key, String(value));
    };

    const handleFormSubmit = async () => {
        const errors = await formik.validateForm();
        if (Object.keys(errors).length > 0) {
            formik.setTouched(
                Object.keys(formik.values).reduce<Record<string, boolean>>((acc, key) => {
                    acc[key] = true;
                    return acc;
                }, {})
            );
            const firstError = Object.values(errors)[0];
            toast.error(typeof firstError === 'string' ? firstError : 'Revisa los campos del formulario');
            return;
        }
        formik.handleSubmit();
    };

    // FUNCTIONS

    //-----------------------------------------------------------------------------------------------------------------
    /**
     * @ES CREAR UN NUEVA CARROÑA
     * @EN CREATE A NEW CARRION
     * 
     * @param values
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleCreate = async (values: any) => {
        try {
            const formData = new FormData();

            if (values.photos) {
                for (let i = 0; i < values.photos.length; i++) {
                    formData.append(`photos[${i}]`, values.photos[i]);
                }
            }

            Object.entries(values).forEach(([key, value]: [string, unknown]) => {
                if (key !== 'photos') {
                    appendFormValue(formData, key, value);
                }
            });
            
            const response = await (await service.createCarrion(formData)).getResponseData();
            if (response.success) {
                refetch();
                onClose();
                formik.resetForm();
                toast.success('Carroña creada correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-----------------------------------------------------------------------------------------------------------------
    
    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES EDITAR UN PARQUE
     * @EN EDIT A PARK
     * 
     * @param values
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleEdit = async (values: any) => {
        try {
            const formData = new FormData();

            Object.entries(values).forEach(([key, value]: [string, unknown]) => {
                if (key !== 'photos') {
                    appendFormValue(formData, key, value);
                }
            });
            
            if (values.photos) {
                for (let i = 0; i < values.photos.length; i++) {
                    formData.append(`photos[${i}]`, values.photos[i]);
                }
            }

            const response = await (await service.editCarrion(formData)).getResponseData();
            if (response.success) {
                refetch();
                onClose();
                formik.resetForm();
                toast.success('Carroña editada correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJAR CAMBIO DE FECHA
     * @EN HANDLE DATE CHANGE
     * 
     * @param date
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleDateChange = (date: any) => {
        const formattedDate = moment(date).format('DD/MM/YYYY');
        formik.setFieldValue('dateReported', formattedDate);
    };
    //-------------------------------------------------------------------------------------------------------------
    
    // USE EFFECT

    useEffect(() => {
        if (isEdit) {
            formik.setFieldValue('id', data?.id);
            formik.setFieldValue('parkId', data?.park.id);
            formik.setFieldValue('windTurbineId', data?.windTurbine ? data?.windTurbine.id : '');
            formik.setFieldValue('userId', data?.user.id);
            formik.setFieldValue('specieId', data?.species.id);
            formik.setFieldValue('statusId', data?.carrionStatus.id);
            formik.setFieldValue('company', data?.company?.id);
            const windSpeed = data?.windSpeed;
            const windDirection =
                windSpeed === 0 || windSpeed === '0' || data?.windDirection === 'CALMA'
                    ? 'CALMA'
                    : data?.windDirection;
            formik.setFieldValue('windDirection', windDirection);
            formik.setFieldValue('windSpeed', String(windSpeed === 0 || windSpeed === '0' ? 0 : windSpeed));
            formik.setFieldValue('cloudCoverage', String(data?.cloudCoverage));
            let coordinates = data?.coordinates.split(',');
            formik.setFieldValue('huso',coordinates[0]);
            formik.setFieldValue('coordinatesX', coordinates[1]);
            formik.setFieldValue('coordinatesY', coordinates[2]);
            // Formatea la fecha al cargar los datos para editar
            // Verificar si dateReported viene como objeto o como string
            if (data?.dateReported) {
                if (typeof data.dateReported === 'string') {
                    // Si viene como string DD/MM/YYYY, usarlo directamente
                    formik.setFieldValue('dateReported', data.dateReported);
                } else if (data.dateReported.date) {
                    // Si viene como objeto con propiedad date, formatear
                    formik.setFieldValue('dateReported', moment(data.dateReported.date).format('DD/MM/YYYY'));
                }
            }
            formik.setFieldValue('comments', data?.comments);
            formik.setFieldValue('notifiedEnvAgency', data?.notifiedEnvAgency);
            formik.setFieldValue('notifiedOwner', data?.notifiedOwner);
            formik.setFieldValue('nameOwner', data?.nameOwner);
            formik.setFieldValue('avianPresencia', data?.avianPresencia);
            formik.setFieldValue('distanceFromTurbine', data?.distanceFromTurbine);
            formik.setFieldValue('turbineOff', data?.turbineOff);
            formik.setFieldValue('age', data?.age);
            formik.setFieldValue('sex', data?.sex);
            
        }
        else{
            formik.resetForm();
        }
    }, [data]);

    // RENDER

    return (
        <Modal
            isOpen={isOpen}
            onClose={() => {
                formik.resetForm();
                onClose()
            }}
            size='xl'
            overlayClassName='backdrop-blur'
            containerClassName='!max-w-[800px] !shadow-xl'
            className='z-[9999] [&_.pointer-events-none]:overflow-visible'
        >
            <div className='m-auto px-7 pt-6 pb-4'>
                <div className='mb-7 flex items-center justify-between'>
                    <Text className="text-xl">{isEdit ? 'Editar ' : 'Crear '} carroña</Text>
                    <ActionIcon size='sm' variant='text' onClick={() => { 
                        formik.resetForm();
                        onClose(); 
                    }}>
                        <IoClose className='h-auto w-6' strokeWidth={1.8} />
                    </ActionIcon>
                </div>

                {!isEdit && (
                    <EntitySelectors formik={formik} isFromPark={isFromPark} speciesAvailabilityFilter={{ carrionAvailable: true }} />
                )}

                {/*  STATUS SELECTOR */}

                {isEdit && (
                        <CustomSelect
                            isSearchable
                            id={'statusId'}
                            label="Estado"
                            options={getCarrionStatusList()}
                            onChange={(e: any) => {
                                formik.setFieldValue('statusId', e.value)
                            }}
                            value={
                                getCarrionStatusList().filter((status: any) => status.value === formik.values.statusId)
                            }
                            required
                            className="mb-4"
                        />
                    )}

                <WeatherSelectors formik={formik} required syncCalmWind />

                <CoordinatesInputs formik={formik} />

                <div className="grid md:grid-cols-2 grid-cols-1 gap-4 mt-2">
           
                    <div className="flex flex-col mb-4">
                        <FormLabel label="Fecha de reporte" required> 
                            <DatePicker
                                className={formik.errors.dateReported && formik.touched.dateReported ? 'invalid-form-input w-full' : 'w-full'}
                                placeholderText="Selecciona la fecha"
                                selected={formik.values.dateReported ? moment(formik.values.dateReported, 'DD/MM/YYYY').toDate() : null}
                                onChange={handleDateChange}
                                dateFormat="dd/MM/yyyy"
                                required
                            />    
                        </FormLabel>
                        {formik.errors.dateReported && formik.touched.dateReported && (
                            <Text className="text-red-500 text-sm mt-1">
                                {formik.errors.dateReported}
                            </Text>
                        )} 
                    </div>

                    <CustomSelect
                        isSearchable
                        id={'age'}
                        label="Edad del ejemplar"
                        options={age}
                        onChange={(e: any) => {
                            formik.setFieldValue('age', e.value)
                        }}
                        value={
                            formik.values.age
                                ? age.find(option => option.value == formik.values.age)
                                : null
                        }
                        required
                        className="mb-4"
                        error={formik.errors.age && formik.touched.age ? formik.errors.age : undefined}

                    />

                </div>

                <div className="grid md:grid-cols-1 grid-cols-1 gap-4 mt-2">
                    <RequiredInput
                        id='distanceFromTurbine'
                        label="Distancia al aerogenerador"
                        formik={formik}
                        required
                        className="mb-4"
                        type={'number'}
                        min={0}
                    />
                </div>
                
                {userCan('list', 'companies') ? (
                    <CustomSelect
                        isSearchable
                        id={'company'}
                        label="Compañia"
                        options={getCompaniesList()}
                        onChange={(e: any) => { 
                            formik.setFieldValue('company', e.value)
                        }}
                        value={
                            getCompaniesList().filter((company: any) => company.value === formik.values.company)
                        }
                        className="mb-4"
                    />
                ) : null}

                <FormLabel label="Observaciones" required> 
                    <Textarea
                        id='comments'
                        label=''
                        rows={6}
                        value={formik.values.comments}
                        onChange={(e) => formik.setFieldValue('comments', e.target.value)}
                        onBlur={formik.handleBlur}
                        className="mb-4"
                    />
                </FormLabel>
                {formik.errors.comments && formik.touched.comments && (
                    <Text className="text-red-500 text-sm mt-1 mb-4">
                        {formik.errors.comments as string}
                    </Text>
                )}


                <div className="grid md:grid-cols-2 grid-cols-1 gap-4 mt-2">

                    <Switch
                        id='turbineOff'
                        label="Parada de aerogenerador"
                        checked={formik.values.turbineOff}
                        onChange={(e) => formik.setFieldValue('turbineOff', e.target.checked)}
                        className="mb-4"
                    />

                    <Switch
                        id='avianPresencia'
                        label="Presencia de aves"
                        checked={formik.values.avianPresencia}
                        onChange={(e) => formik.setFieldValue('avianPresencia', e.target.checked)}
                        className="mb-4"
                    />

                    <Switch
                        id='notifiedEnvAgency'
                        label="Notificado a AMA"
                        checked={formik.values.notifiedEnvAgency}
                        onChange={(e) => formik.setFieldValue('notifiedEnvAgency', e.target.checked)}
                        className="mb-4"
                    />

                    <Switch
                        id='notifiedOwner'
                        label="Notificado al propietario"
                        checked={formik.values.notifiedOwner}
                        onChange={(e) => {
                            formik.setFieldValue('notifiedOwner', e.target.checked)
                            if(!e.target.checked) {
                                formik.setFieldValue('nameOwner', '')
                            }
                        }}
                        className="mb-4"
                    />

                    {formik.values.notifiedOwner === true ? (
                        <Input
                            label="Nombre del propietario"
                            placeholder="Nombre del propietario"
                            value={formik.values.nameOwner}
                            onChange={(e) => formik.setFieldValue('nameOwner', e.target.value)}
                        />
                    ) : null}

                </div>


                <div className='mb-4 mt-2'>
                    <FormLabel label="Fotos de la carroña"> 
                        <input
                            type="file"
                            id="photos"
                            name="photos"
                            accept="image/*"
                            multiple
                            onChange={(event) => {
                                const files = event?.currentTarget?.files ? event.currentTarget.files : null;
                                formik.setFieldValue("photos", files);
                            }}
                            className='mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700'
                        />
                        {formik.errors.photos && formik.touched.photos && (
                            <Text className="text-red-500 text-sm mt-1">
                                {formik.errors.photos}
                            </Text>
                        )}
                    </FormLabel>
                </div>


            </div>

            <div className='flex justify-center p-7'>
                <Button
                    disabled={formik.isSubmitting}
                    onClick={handleFormSubmit}
                    color='primary'
                >
                    {isEdit ? 'Editar' : 'Crear'}
                </Button>
            </div>
        </Modal>
    )
}


export default CarrionFormModal;
